import { Injectable } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { IzzyMetadataEngine } from 'mj_generatedentities';
import { BCMembershipService } from '@bluecypress/bcsaas-entities-extended';
import { BC_ROLE_LEVELS } from '@bluecypress/bcsaas-core';

/**
 * Service for centralized authentication and role-based access control.
 * Provides methods to check if the current user is a system developer (internal org member).
 */
@Injectable({ providedIn: 'root' })
export class IzzyAuthService {
  private static readonly INTERNAL_ORG_DOMAIN = 'meetizzy.ai';

  private _isSystemDeveloper: boolean | null = null;
  private _checkPromise: Promise<boolean> | null = null;
  private _isOrgAdmin: boolean | null = null;

  /**
   * Checks if the current user is a system developer (member of internal org with Owner/Admin role).
   * Results are cached after the first check.
   * @returns Promise<boolean> - true if user is a system developer
   */
  async isSystemDeveloper(): Promise<boolean> {
    // Return cached result if available
    if (this._isSystemDeveloper !== null) {
      return this._isSystemDeveloper;
    }

    // If a check is already in progress, wait for it
    if (this._checkPromise) {
      return this._checkPromise;
    }

    // Start the check
    this._checkPromise = this.checkInternalOrgMembership();

    try {
      this._isSystemDeveloper = await this._checkPromise;
      return this._isSystemDeveloper;
    } finally {
      this._checkPromise = null;
    }
  }

  /**
   * Checks if the current user has Level >= ADMIN in any organization.
   * Results are cached after the first check.
   *
   * Delegates to BCMembershipService.HasMembershipLevel — single server-side
   * query per org with RLS applied.
   */
  async isOrgAdmin(): Promise<boolean> {
    if (this._isOrgAdmin !== null) {
      return this._isOrgAdmin;
    }

    try {
      await IzzyMetadataEngine.Instance.Config(false);
      const engine = IzzyMetadataEngine.Instance;
      const contactId = engine.CurrentContactID;
      if (!contactId) {
        this._isOrgAdmin = false;
        return false;
      }

      // Prefer the middleware-stamped tenant context via the engine. The old
      // per-org HasMembershipLevel loop is tenant-filtered from the browser
      // since BCSaaS v1.5 (SkipTenantFilter doesn't survive GraphQL transport),
      // so it can only ever confirm the current context org.
      if (engine.CurrentUserTenantMemberships) {
        this._isOrgAdmin = engine.CurrentUserHasMembershipLevel(BC_ROLE_LEVELS.ADMIN);
        return this._isOrgAdmin;
      }

      const orgIds = Array.from(new Set(
        engine.OrganizationContacts
          .filter(oc => oc.PersonID === contactId)
          .map(oc => oc.OrganizationID)
      ));
      const contextUser = Metadata.Provider.CurrentUser;

      for (const orgId of orgIds) {
        const ok = await BCMembershipService.HasMembershipLevel(
          contactId,
          orgId,
          BC_ROLE_LEVELS.ADMIN,
          contextUser
        );
        if (ok) {
          this._isOrgAdmin = true;
          return true;
        }
      }
      this._isOrgAdmin = false;
      return false;
    } catch (error) {
      console.error('Error checking org admin status:', error);
      this._isOrgAdmin = false;
      return false;
    }
  }

  /**
   * Clears the cached system developer status, forcing a re-check on next call.
   */
  clearCache(): void {
    this._isSystemDeveloper = null;
    this._checkPromise = null;
    this._isOrgAdmin = null;
  }

  /**
   * Checks if the current user is a member of the internal organization at Level >= ADMIN.
   *
   * Post-BAC: the internal org is identified by __BCSaaS.OrganizationConfig.Domain
   * (pre-BAC this was __BCSaaS.Organization.Domain; the BAC lift moved per-org config
   * into __BCSaaS.OrganizationConfig). Membership is verified via
   * BCMembershipService.HasMembershipLevel — single server-side query with RLS applied.
   */
  private async checkInternalOrgMembership(): Promise<boolean> {
    try {
      await IzzyMetadataEngine.Instance.Config(false);
      const engine = IzzyMetadataEngine.Instance;

      const currentContactId = engine.CurrentContactID;
      if (!currentContactId) {
        return false;
      }

      const target = IzzyAuthService.INTERNAL_ORG_DOMAIN;
      const rv = new RunView();
      const result = await rv.RunView<{ OrganizationID: string }>({
        EntityName: 'BC: Organization Configs',
        ExtraFilter: `Domain = '${target}'`,
        ResultType: 'simple',
        MaxRows: 1
      });
      const internalOrgId = result.Success ? result.Results?.[0]?.OrganizationID : undefined;
      if (!internalOrgId) {
        return false;
      }

      return await BCMembershipService.HasMembershipLevel(
        currentContactId,
        internalOrgId,
        BC_ROLE_LEVELS.ADMIN,
        Metadata.Provider.CurrentUser
      );
    } catch (error) {
      console.error('Error checking internal org membership:', error);
      return false;
    }
  }
}
